/* GASSISA
=========================================================================================
* Descripción: Tabla editable de nómina con columnas dinémicas basadas en conceptos
* de bonificación/deducción. Navegación por teclado estilo Etiquetado.
* El campo bonosJson (JSON) por empleado almacena los valores de las columnas dinémicas.
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1       | 22/08/2026 | Lic. José Luis Aragón Cervantes  | Creación
*      2       | 22/08/2026 | Lic. José Luis Aragón Cervantes  | Usa bonosJson como campo de almacenamiento por empleado
*      3       | 22/08/2026 | Lic. José Luis Aragón Cervantes  | Celda editable aislada por empleado+concepto, evita mezcla de datos
* ---------------------------------------------------------------------------------------
*/

import TextField from "../ui/TextField";
import { forwardRef, useCallback, useMemo, useRef, useState } from "react";
import { Box, InputAdornment, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TableSortLabel, Typography } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

const normalizeSearch = (value) => String(value ?? "").toLowerCase().trim();

const getConceptoValor = (bonosJson, tipo, descripcion) => {
    const arr = Array.isArray(bonosJson) ? bonosJson : [];
    const encontrado = arr.find(
        (c) =>
            String(c?.tipo || "").toUpperCase() === String(tipo).toUpperCase() &&
            String(c?.descripcion || "").trim().toLowerCase() ===
                String(descripcion || "").trim().toLowerCase()
    );
    return Number(encontrado?.valor) || 0;
};

const CeldaEditableConcepto = forwardRef(function CeldaEditableConcepto(
    { valorInicial, esBono, onKeyDown, inputKey, onCommit },
    ref
) {
    const [rawValue, setRawValue] = useState(String(valorInicial || ""));

    const handleChange = (e) => {
        setRawValue(e.target.value);
    };

    const handleBlur = (e) => {
        onCommit(e.target.value);
    };

    const handleFocus = (e) => {
        e.target.select();
    };

    const handleClick = (e) => {
        e.target.select();
    };

    return (
        <TextField autoComplete="off"
            variant="standard"
            type="text"
            inputMode="numeric"
            enterKeyHint="enter"
            size="small"
            value={rawValue}
            onChange={handleChange}
            onBlur={handleBlur}
            onKeyDown={onKeyDown}
            onFocus={handleFocus}
            onClick={handleClick}
            inputRef={ref}
            inputProps={{
                min: 0,
                style: {
                    textAlign: "right",
                    fontSize: "0.85rem",
                    padding: "2px 4px",
                },
            }}
            sx={{
                width: 100,
                "& .MuiInput-root": {
                    fontSize: "0.85rem",
                },
            }}
        />
    );
});

export default function SizeSoftXTablaNominasEditable({
    columns = [],
    esquema = [],
    data = [],
    id = "usuario",
    actions = null,
    onEditarConcepto = null,
    disablePagination = false,
    rowsPerPageInitial = 25,
    searchPlaceholder = "Buscar registro...",
    emptyMessage = "No hay registros para mostrar",
}) {
    const [busqueda, setBusqueda] = useState("");
    const [ordenCampo, setOrdenCampo] = useState("");
    const [ordenDir, setOrdenDir] = useState("asc");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageInitial);

    const inputRefs = useRef({});

    const handleCambiarOrden = (campo) => {
        const esAsc = ordenCampo === campo && ordenDir === "asc";
        setOrdenDir(esAsc ? "desc" : "asc");
        setOrdenCampo(campo);
        setPage(0);
    };

    const registrosFiltrados = useMemo(() => {
        const keyword = normalizeSearch(busqueda);
        let rows = (data || []).slice();

        if (keyword) {
            rows = rows.filter((fila) =>
                columns.some((col) => normalizeSearch(fila?.[col.field]).includes(keyword)) ||
                (fila.nombre || "").toLowerCase().includes(keyword) ||
                (fila.usuario || "").toLowerCase().includes(keyword)
            );
        }

        if (ordenCampo) {
            rows = [...rows].sort((a, b) => {
                const av = a?.[ordenCampo];
                const bv = b?.[ordenCampo];
                if (av == null) return 1;
                if (bv == null) return -1;
                if (typeof av === "number" && typeof bv === "number") {
                    return ordenDir === "asc" ? av - bv : bv - av;
                }
                const cmp = String(av).localeCompare(String(bv), undefined, { numeric: true });
                return ordenDir === "asc" ? cmp : -cmp;
            });
        }

        return rows;
    }, [busqueda, data, columns, ordenCampo, ordenDir]);

    const filasPagina = !disablePagination
        ? registrosFiltrados.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
        : registrosFiltrados;

    const handleCambiarPage = (_, nuevaPage) => setPage(nuevaPage);
    const handleCambiarRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const conceptoKeys = useMemo(() => {
        return (esquema || []).map((c, i) => ({
            index: i,
            tipo: String(c.tipo || "").toUpperCase(),
            descripcion: String(c.descripcion || "").trim(),
            key: `${String(c.tipo || "").toUpperCase()}-${String(c.descripcion || "").trim()}`,
        }));
    }, [esquema]);

    const getAllEditableKeys = useCallback(() => {
        const keys = [];
        filasPagina.forEach((fila, rowIndex) => {
            conceptoKeys.forEach((concepto) => {
                keys.push({
                    rowId: fila[id],
                    rowIndex,
                    conceptIndex: concepto.index,
                    tipo: concepto.tipo,
                    descripcion: concepto.descripcion,
                    key: concepto.key,
                    inputKey: `${fila[id]}-${concepto.key}`,
                });
            });
        });
        return keys;
    }, [filasPagina, conceptoKeys, id]);

    const handleInputKeyDown = useCallback(
        (e, filaId, conceptoKey) => {
            const key = e.key;
            if (key !== "Tab" && key !== "Enter" && key !== "ArrowDown" && key !== "ArrowUp" && key !== "Escape") {
                return;
            }

            if (key === "Escape") {
                e.target.blur();
                return;
            }

            e.preventDefault();
            e.stopPropagation();

            const allKeys = getAllEditableKeys();
            const currentInputKey = `${filaId}-${conceptoKey}`;
            const currentIndex = allKeys.findIndex((k) => k.inputKey === currentInputKey);

            if (currentIndex === -1) return;

            let nextIndex;

            if (key === "ArrowDown") {
                const mismaColumna = allKeys.filter((k) => k.key === conceptoKey);
                const posEnColumna = mismaColumna.findIndex((k) => k.inputKey === currentInputKey);
                if (posEnColumna < mismaColumna.length - 1) {
                    nextIndex = allKeys.indexOf(mismaColumna[posEnColumna + 1]);
                } else {
                    return;
                }
            } else if (key === "ArrowUp") {
                const mismaColumna = allKeys.filter((k) => k.key === conceptoKey);
                const posEnColumna = mismaColumna.findIndex((k) => k.inputKey === currentInputKey);
                if (posEnColumna > 0) {
                    nextIndex = allKeys.indexOf(mismaColumna[posEnColumna - 1]);
                } else {
                    return;
                }
            } else {
                if (e.shiftKey && key === "Tab") {
                    nextIndex = currentIndex - 1;
                    if (nextIndex < 0) nextIndex = allKeys.length - 1;
                } else {
                    nextIndex = currentIndex + 1;
                    if (nextIndex >= allKeys.length) {
                        nextIndex = 0;
                    }
                }
            }

            const nextKey = allKeys[nextIndex];
            if (!nextKey) return;
            const nextInput = inputRefs.current[nextKey.inputKey];
            if (nextInput) {
                nextInput.focus();
                setTimeout(() => nextInput.select(), 0);
            }
        },
        [getAllEditableKeys]
    );

    const handleCommit = useCallback(
        (fila, concepto, rawValue) => {
            if (!onEditarConcepto) return;
            const parsed = parseFloat(rawValue);
            const valor = isNaN(parsed) ? 0 : parsed;
            const bonosArray = Array.isArray(fila.bonosJson) ? fila.bonosJson : [];
            const conceptoActual = getConceptoValor(bonosArray, concepto.tipo, concepto.descripcion);
            if (conceptoActual === valor) return;

            const nuevosConceptos = bonosArray.map((c) => {
                const coincideTipo = String(c?.tipo || "").toUpperCase() === concepto.tipo;
                const coincideDesc =
                    String(c?.descripcion || "").trim().toLowerCase() === concepto.descripcion.toLowerCase();
                if (coincideTipo && coincideDesc) {
                    return { ...c, valor };
                }
                return c;
            });

            const conceptoExistia = bonosArray.some(
                (c) =>
                    String(c?.tipo || "").toUpperCase() === concepto.tipo &&
                    String(c?.descripcion || "").trim().toLowerCase() === concepto.descripcion.toLowerCase()
            );

            const bonosFinales = conceptoExistia
                ? nuevosConceptos
                : [...nuevosConceptos, { tipo: concepto.tipo, descripcion: concepto.descripcion, valor }];

            onEditarConcepto(fila, bonosFinales);
        },
        [onEditarConcepto]
    );

    const handleInputRef = useCallback(
        (inputKey) => (el) => {
            if (el) {
                inputRefs.current[inputKey] = el;
            } else {
                delete inputRefs.current[inputKey];
            }
        },
        []
    );

    const renderCelda = (fila, col) => {
        if (col.render) return col.render(fila);
        return fila?.[col.field] ?? "";
    };

    const totalColumnas = columns.length + conceptoKeys.length + (actions ? 1 : 0);

    return (
        <Paper variant="outlined" sx={{ width: "100%" }}>
            <Box sx={{ p: 1, display: "flex", alignItems: "center", gap: 1 }}>
                <TextField autoComplete="off"
                    size="small"
                    placeholder={searchPlaceholder}
                    value={busqueda}
                    onChange={(e) => {
                        setBusqueda(e.target.value);
                        setPage(0);
                    }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        ),
                    }}
                    sx={{ maxWidth: 340 }}
                />
            </Box>
            <TableContainer
                sx={
                    disablePagination
                        ? { overflowX: "auto" }
                        : { maxHeight: "60vh", overflow: "auto" }
                }
            >
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow>
                            {columns.map((col) => (
                                <TableCell
                                    key={col.field || col.title}
                                    align={col.align || "left"}
                                    sortDirection={ordenCampo === col.field ? ordenDir : false}
                                    sx={{ whiteSpace: "nowrap", minWidth: col.width }}
                                >
                                    {col.sortable === false || !col.field ? (
                                        col.title
                                    ) : (
                                        <TableSortLabel
                                            active={ordenCampo === col.field}
                                            direction={ordenCampo === col.field ? ordenDir : "asc"}
                                            onClick={() => handleCambiarOrden(col.field)}
                                        >
                                            {col.title}
                                        </TableSortLabel>
                                    )}
                                </TableCell>
                            ))}
                            {conceptoKeys.map((c) => {
                                const esBono = c.tipo === "BONO";
                                return (
                                    <TableCell
                                        key={c.key}
                                        align="right"
                                        sx={{
                                            whiteSpace: "nowrap",
                                            minWidth: 110,
                                            backgroundColor: esBono ? "#e8f5e9" : "#fce4ec",
                                            fontWeight: 700,
                                            fontSize: "0.8rem",
                                        }}
                                    >
                                        <span style={{ color: esBono ? "#2e7d32" : "#c62828" }}>
                                            {c.descripcion}
                                        </span>
                                    </TableCell>
                                );
                            })}
                            {actions && <TableCell align="center" sx={{ minWidth: 260 }}>Acciones</TableCell>}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filasPagina.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={totalColumnas} align="center" sx={{ py: 3 }}>
                                    <Typography color="text.secondary">{emptyMessage}</Typography>
                                </TableCell>
                            </TableRow>
                        )}
                        {filasPagina.map((fila) => (
                            <TableRow
                                hover
                                key={fila?.[id] || Math.random()}
                            >
                                {columns.map((col) => (
                                    <TableCell
                                        key={col.field || col.title}
                                        align={col.align || "left"}
                                        sx={{ whiteSpace: "nowrap" }}
                                    >
                                        {renderCelda(fila, col)}
                                    </TableCell>
                                ))}
                                {conceptoKeys.map((concepto) => {
                                    const valor = getConceptoValor(
                                        fila.bonosJson,
                                        concepto.tipo,
                                        concepto.descripcion
                                    );
                                    const cellKey = `${fila[id]}-${concepto.key}`;
                                    const esBono = concepto.tipo === "BONO";
                                    return (
                                        <TableCell
                                            key={concepto.key}
                                            align="right"
                                            sx={{
                                                p: 0.25,
                                                backgroundColor: esBono ? "#f1f8e9" : "#fce4ec",
                                            }}
                                        >
                                            <CeldaEditableConcepto
                                                key={cellKey}
                                                valorInicial={valor}
                                                esBono={esBono}
                                                inputKey={cellKey}
                                                ref={handleInputRef(cellKey)}
                                                onKeyDown={(e) =>
                                                    handleInputKeyDown(e, String(fila[id]), concepto.key)
                                                }
                                                onCommit={(rawValue) =>
                                                    handleCommit(fila, concepto, rawValue)
                                                }
                                            />
                                        </TableCell>
                                    );
                                })}
                                {actions && (
                                    <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                                        {actions(fila)}
                                    </TableCell>
                                )}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            {!disablePagination && (
                <TablePagination
                    component="div"
                    rowsPerPageOptions={[5, 10, 25, 100]}
                    count={registrosFiltrados.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    labelRowsPerPage="Registros por pégina"
                    onPageChange={handleCambiarPage}
                    onRowsPerPageChange={handleCambiarRowsPerPage}
                />
            )}
        </Paper>
    );
}
